/**
 * Service for building context from chat message history
 * Processes the complete message history and constructs a coherent context string
 */
export class ContextBuilderService {
    constructor() {
        /**
         * Maximum number of messages to include in the context
         * This helps prevent exceeding token limits
         */
        Object.defineProperty(this, "MAX_MESSAGES", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 100
        });
        /**
         * Maximum length of each message in the context (in characters)
         * Messages longer than this will be truncated
         */
        Object.defineProperty(this, "MAX_MESSAGE_LENGTH", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 2000
        });
    }
    /**
     * Builds a context string from the chat message history
     * @param messages Array of chat messages
     * @returns Formatted context string for LLM consumption
     */
    buildContext(messages) {
        if (!messages || messages.length === 0) {
            return '';
        }
        // Get current time
        const now = new Date();
        const currentTime = now.toLocaleString('en-US', {
            timeZone: 'Europe/Berlin',
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZoneName: 'short'
        });
        // Take only the most recent messages up to MAX_MESSAGES
        const recentMessages = messages.slice(-this.MAX_MESSAGES);
        // Format each message and join them into a single context string
        return `We are in: Germany, Frankfurt\n\n` + `Current Date: ${currentTime}\n\n` + 'Recent Chat Messages: \n\n' + recentMessages
            .map(message => this.formatMessage(message))
            .join('\n\n');
    }
    /**
     * Formats a single message for inclusion in the context
     * @param message The chat message to format
     * @returns Formatted message string
     */
    formatMessage(message) {
        // Truncate message content if it's too long
        const content = this.truncateContent(message.content);
        // Format differently based on whether it's an AI or user message
        if (message.is_ai_message) {
            return `Assistant: ${content}`;
        }
        else {
            return `User: ${content}`;
        }
    }
    /**
     * Truncates message content if it exceeds the maximum length
     * @param content The message content
     * @returns Truncated content
     */
    truncateContent(content) {
        if (content.length <= this.MAX_MESSAGE_LENGTH) {
            return content;
        }
        // Truncate and add ellipsis
        return content.substring(0, this.MAX_MESSAGE_LENGTH - 3) + '...';
    }
    /**
     * Optimizes the context by removing less relevant messages when approaching token limits
     * @param messages Array of chat messages
     * @param maxTokenEstimate Maximum token estimate to aim for
     * @returns Optimized array of messages
     */
    optimizeContext(messages, maxTokenEstimate = 3000) {
        if (!messages || messages.length === 0) {
            return [];
        }
        // Simple token estimation (rough approximation)
        const estimateTokens = (text) => {
            // Rough estimate: 1 token ≈ 4 characters for English text
            return Math.ceil(text.length / 4);
        };
        // Start with the most recent message
        const optimizedMessages = [];
        let tokenCount = 0;
        // Process messages from newest to oldest
        for (let i = messages.length - 1; i >= 0; i--) {
            const message = messages[i];
            const messageTokens = estimateTokens(message.content);
            // If adding this message would exceed the limit, stop
            if (tokenCount + messageTokens > maxTokenEstimate && optimizedMessages.length > 0) {
                break;
            }
            // Add the message to the optimized list and update token count
            optimizedMessages.unshift(message);
            tokenCount += messageTokens;
        }
        return optimizedMessages;
    }
}
// Export a singleton instance
export const contextBuilderService = new ContextBuilderService();
